import { ChangeDetectionStrategy, Component, HostListener, OnDestroy, computed, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { lucideChevronLeft, lucideChevronRight } from '@ng-icons/lucide';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmTooltipTriggerDirective } from '@spartan-ng/helm/tooltip';
import { AnalysisViewer, ViewerSelection } from '@shared/components/analysis-viewer/analysis-viewer';
import { StatePanel } from '@shared/components/state-panel/state-panel';
import { TabItem, Tabs } from '@shared/components/tabs/tabs';
import { Analysis, AnalysisPlanning, AnalysisPolygon, FileOverviewItem } from '@shared/models/analysis.model';
import { AnalysisService } from '@shared/services/analysis.service';
import { ToastService } from '@shared/services/toast.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { CampaignNav, CampaignWorkflowStore } from '../../store/campaign-workflow.store';
import { CampaignsService } from '../../services/campaigns.service';
import { FilterRail, RailGroup } from '@shared/components/filter-rail/filter-rail';
import { StageChip } from '../stage-chip/stage-chip';
import { EXCLUDE_REASONS, PLANNING_OUTCOMES, REVIEW_STAGES, STAGE_META, WorkItem, WorkflowStage, marksOf } from '../../models/campaign-workflow.model';

type Queue = WorkflowStage | 'recheck' | 'selection';
type Decision = 'ok' | 'fix' | 'later' | 'seen' | 'out' | 'plan';

/** One session looks at this many at a time; the end screen offers the next ones. */
const QUEUE_SIZE = 500;
const PREFETCHED = 6;
const QUEUE_GROUPS: { label: string; queues: { id: Queue; label: string; tip: string }[] }[] = [
  { label: 'Prüfen', queues: [
    { id: 'review', label: 'Ungeprüft', tip: 'Noch nicht angesehen' },
    { id: 'seen', label: 'Angesehen', tip: 'Angesehen, nicht entschieden' },
    { id: 'flagged', label: 'Vorgemerkt', tip: 'Stimmt nicht, soll korrigiert werden' },
  ] },
  { label: 'Danach', queues: [
    { id: 'replan', label: 'Zu planen', tip: 'Korrigiert oder ohne Planung' },
    { id: 'recheck', label: 'Nachkontrolle', tip: 'Neu geplant, freiwillig noch einmal ansehen' },
  ] },
];
const QUEUES = QUEUE_GROUPS.flatMap((group) => group.queues);
const RUN_ROW = 'run';
/** The same narrowing as in Planungsergebnisse, within the list being worked through. */
const NARROW: { label: string; rows: { id: string; label: string; test: (item: WorkItem) => boolean }[] }[] = [
  { label: 'Planung', rows: [
    { id: 'planned', label: 'Erfolgreich', test: (item) => item.planning.outcome === 'ok' },
    { id: 'outdated', label: 'Veraltet', test: (item) => item.planning.outcome === 'outdated' },
    { id: 'failed', label: 'Fehlgeschlagen', test: (item) => item.planning.outcome === 'failed' },
    { id: 'none', label: 'Ohne Planung', test: (item) => item.planning.outcome === 'none' },
  ] },
  { label: 'Merkmal', rows: [
    { id: 'corrected', label: 'Korrigiert', test: (item) => item.corrections > 0 },
    { id: 'replanned', label: 'Nachgeplant', test: (item) => item.replanned },
  ] },
  { label: 'Amortisation', rows: [
    { id: 'fast', label: 'Unter 10 Jahren', test: (item) => item.planning.payback !== null && item.planning.payback < 10 },
    { id: 'mid', label: '10 bis 15 Jahre', test: (item) => item.planning.payback !== null && item.planning.payback >= 10 && item.planning.payback < 15 },
    { id: 'slow', label: 'Über 15 Jahre', test: (item) => item.planning.payback !== null && item.planning.payback >= 15 },
  ] },
];
const NARROW_ROWS = NARROW.flatMap((group) => group.rows);
const LOAD_FAILED = 'Der Server hat nicht rechtzeitig geantwortet. Ihre bisherigen Entscheidungen sind gespeichert.';
const DECISIONS: Record<Decision, { label: string; color: string }> = {
  ok: { label: 'stimmt', color: 'var(--s-fertig)' },
  fix: { label: 'korrigiert', color: 'var(--s-zuplanen)' },
  later: { label: 'später', color: 'var(--s-vorgemerkt)' },
  seen: { label: 'angesehen', color: 'var(--s-zurueck)' },
  out: { label: 'zurückgestellt', color: 'var(--s-zurueck)' },
  plan: { label: 'neu geplant', color: 'var(--s-fertig)' },
};

interface Bundle {
  analysis: Analysis;
  files: FileOverviewItem[];
  polygons: AnalysisPolygon[];
  plannings: AnalysisPlanning[];
}

/**
 * The work surface: go through results with the keyboard, correct them right in
 * the picture. Nothing is stored until F or → says so, Esc throws edits away.
 */
@Component({
  selector: 'app-campaign-result-review-tab',
  standalone: true,
  imports: [DecimalPipe, NgIconComponent, HlmButtonDirective, HlmTooltipTriggerDirective, StatePanel, Tabs, AnalysisViewer, FilterRail, StageChip],
  providers: [provideIcons({ lucideChevronLeft, lucideChevronRight })],
  templateUrl: './campaign-result-review-tab.html',
  host: { class: 'flex min-h-0 flex-1 flex-col' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignResultReviewTab implements OnDestroy {
  readonly store = inject(CampaignWorkflowStore);
  private readonly api = inject(CampaignsService);
  private readonly analyses = inject(AnalysisService);
  private readonly org = inject(CurrentOrganizationStore);
  private readonly toast = inject(ToastService);
  readonly viewer = viewChild(AnalysisViewer);

  readonly excludeReasons = EXCLUDE_REASONS;
  readonly planningView: ViewerSelection = { view: 'image', image: 'planung', model: 'with-pv' };
  readonly outcomes = PLANNING_OUTCOMES;
  readonly outcomeDots: Record<WorkItem['planning']['outcome'], string> = { ok: 'bg-success', failed: 'bg-danger', outdated: 'bg-warning', none: 'bg-ks-grey-300' };

  readonly queue = signal<Queue>('review');
  readonly runId = signal<string | null>(null);
  /** Exactly these analyses, handed over from a table or an execution. */
  readonly selection = signal<string[] | null>(null);
  readonly narrowedBy = signal<string | null>(null);
  /** Everything in the queue; the list is what is left of it after narrowing. */
  private readonly loadedItems = signal<WorkItem[]>([]);
  readonly list = signal<WorkItem[]>([]);
  /** How many the queue holds in all; the session sees at most QUEUE_SIZE of them. */
  readonly base = signal(0);
  readonly pos = signal(0);
  readonly bundle = signal<Bundle | null>(null);
  readonly decisions = signal<Record<string, Decision>>({});
  readonly view = signal<'detection' | 'planning'>('detection');
  readonly excluding = signal(false);
  readonly loaded = signal(false);
  readonly loadingItem = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);

  private readonly bundles = new Map<string, Promise<Bundle>>();
  private request = 0;

  readonly item = computed<WorkItem | null>(() => this.list()[this.pos()] ?? null);
  readonly ended = computed(() => this.loaded() && !this.item());
  readonly orgId = computed(() => this.org.organizationId()!);
  readonly campaignId = computed(() => this.store.campaignId()!);
  readonly canUpdate = computed(() => this.org.can('analysis', 'update'));
  readonly dirty = computed(() => this.viewer()?.hasPendingReviewChanges() ?? false);
  readonly busy = computed(() => this.saving() || this.loadingItem());

  private queueRow(queue: { id: Queue; label: string; tip: string }) {
    const counts = this.store.counts();
    const isRecheck = queue.id === 'recheck';
    return {
      id: queue.id,
      label: queue.label,
      tip: queue.tip,
      count: isRecheck ? this.store.summary()?.recheck ?? 0 : counts?.[queue.id as WorkflowStage] ?? 0,
      color: isRecheck ? STAGE_META.done.color : STAGE_META[queue.id as WorkflowStage].color,
    };
  }

  readonly railGroups = computed<RailGroup[]>(() => {
    const current = this.queue();
    const items = this.loadedItems();
    const selection = this.selection();
    const groups: RailGroup[] = [];
    if (selection) groups.push({ rows: [{ id: 'selection', label: 'Auswahl', count: selection.length, color: 'var(--primary)', tip: 'Genau die Analysen, die Sie ausgewählt haben' }] });
    if (this.runId()) groups.push({ rows: [{ id: RUN_ROW, label: 'Nur diese Berechnung', clearable: true, color: 'var(--primary)', tip: 'Alle Ergebnisse der Kampagne zeigen' }] });
    groups.push(...QUEUE_GROUPS.map((group) => ({ label: group.label, rows: group.queues.map((queue) => this.queueRow(queue)) })));
    // Opened on an analysis outside these lists: that stage shows only then.
    if (current !== 'selection' && !QUEUES.some((queue) => queue.id === current)) {
      groups.push({ rows: [this.queueRow({ id: current, label: STAGE_META[current as WorkflowStage].label, tip: STAGE_META[current as WorkflowStage].tip })] });
    }
    for (const group of NARROW) {
      const rows = group.rows.map(({ id, label, test }) => ({ id, label, count: items.reduce((all, item) => all + (test(item) ? 1 : 0), 0) }));
      if (rows.some((row) => row.count)) groups.push({ label: group.label, rows });
    }
    return groups;
  });
  readonly railActive = computed(() => [this.queue() as string, ...(this.narrowedBy() ? [this.narrowedBy()!] : [])]);

  readonly position = computed(() => {
    const total = this.list().length;
    return total ? `${Math.min(this.pos() + 1, total).toLocaleString('de-DE')} / ${Math.max(this.base(), total).toLocaleString('de-DE')}` : '0 / 0';
  });

  readonly progressPercent = computed(() => {
    const total = this.list().length;
    return total ? ((this.pos() + 1) / total) * 100 : 0;
  });
  readonly viewTabs = computed<TabItem[]>(() => [
    { value: 'detection', label: 'Erkennung', count: this.bundle()?.polygons.length ?? null },
    { value: 'planning', label: 'Planung', disabled: !this.hasPlanning(), disabledReason: 'Noch keine Planung' },
  ]);

  readonly tally = computed(() => {
    const counted: Partial<Record<Decision, number>> = {};
    for (const decision of Object.values(this.decisions())) counted[decision] = (counted[decision] ?? 0) + 1;
    return (Object.entries(counted) as [Decision, number][]).map(([decision, count]) => ({ label: DECISIONS[decision].label, count }));
  });
  readonly tallyText = computed(() => this.tally().map((row) => `${row.count} ${row.label}`).join(' · ') || 'Noch nichts entschieden');

  readonly title = computed(() => {
    const address = this.bundle()?.analysis.address;
    return address?.street ? `${address.street} ${address.houseNumber ?? ''}`.trim() : this.street(this.item()?.title ?? null);
  });
  readonly place = computed(() => {
    const address = this.bundle()?.analysis.address;
    if (address?.city) return [address.postalCode, address.city].filter(Boolean).join(' ');
    const title = this.item()?.title ?? '';
    return title.includes(',') ? title.slice(title.indexOf(',') + 1).trim() : '';
  });
  readonly marks = computed(() => {
    const item = this.item();
    if (!item) return [];
    const marks: string[] = [];
    if (item.corrections) marks.push(item.corrections > 1 ? `Korrigiert ×${item.corrections}` : 'Korrigiert');
    if (item.replanned) marks.push('Nachgeplant');
    return marks;
  });
  readonly hint = computed(() => {
    const item = this.item();
    return item ? marksOf(item).filter((mark) => !mark.startsWith('Korrigiert') && mark !== 'Nachgeplant' && mark !== 'Planung fehlgeschlagen').join(' · ') : '';
  });
  readonly canEdit = computed(() => {
    const stage = this.item()?.stage;
    return this.canUpdate() && !!stage && stage !== 'running' && stage !== 'excluded';
  });
  readonly isCheck = computed(() => {
    const item = this.item();
    return !!item && item.replanned && !item.checked;
  });
  readonly inReview = computed(() => !!this.item() && REVIEW_STAGES.includes(this.item()!.stage));

  /** The planning shown: the current one, or else the latest that worked. */
  readonly planning = computed<AnalysisPlanning | null>(() => {
    const plannings = this.bundle()?.plannings ?? [];
    const id = this.item()?.planning.id;
    return plannings.find((planning) => planning.id === id) ?? plannings.find((planning) => planning.status === 'completed') ?? null;
  });
  readonly hasPlanning = computed(() => !!this.planning());
  readonly planningRows = computed(() => {
    const planning = this.planning();
    if (!planning) return [];
    const one = (value: number) => value.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    const investment = planning.totalInvestment ?? planning.acquisitionCosts;
    return [
      { key: 'Module', value: String(planning.numberModels ?? '–') },
      { key: 'Leistung', value: planning.plantCapacity != null ? `${one(planning.plantCapacity)} kWp` : '–' },
      { key: 'Investition', value: investment != null ? `${Math.round(investment).toLocaleString('de-DE')} €` : '–' },
      { key: 'Amortisation', value: planning.amortizationPeriod != null ? `${one(planning.amortizationPeriod)} Jahre` : '–' },
    ];
  });
  readonly planningMessage = computed(() => {
    const item = this.item();
    if (!item) return null;
    if (item.planning.message) return item.planning.message;
    return item.planning.outcome === 'none' ? 'Bisher nur bis zur Analyse gerechnet.' : null;
  });

  readonly dirtyText = computed(() => {
    const viewer = this.viewer();
    if (!viewer) return '';
    const parts: string[] = [];
    if (viewer.dirtyPolygonIds().size) parts.push('Flächen geändert');
    const removed = viewer.pendingDeleteIds().size;
    if (removed) parts.push(removed === 1 ? '1 Fläche gelöscht' : `${removed} Flächen gelöscht`);
    return parts.join(' · ');
  });

  readonly endActions = computed(() => {
    const counts = this.store.counts();
    const current = this.queue();
    const actions: { label: string; run: () => void }[] = [];
    if (counts?.flagged && current !== 'flagged') actions.push({ label: `${counts.flagged.toLocaleString('de-DE')} Vorgemerkte korrigieren`, run: () => void this.open('flagged') });
    if (counts?.review) actions.push({ label: `${counts.review.toLocaleString('de-DE')} weiter prüfen`, run: () => void this.open('review') });
    if (counts?.seen && current !== 'seen') actions.push({ label: `${counts.seen.toLocaleString('de-DE')} Angesehene entscheiden`, run: () => void this.open('seen') });
    actions.push({ label: 'Zur Übersicht', run: () => void this.store.go('overview') });
    return actions;
  });
  readonly endText = computed(() => {
    const seen = this.list().length > 0;
    const replan = this.store.counts()?.replan ?? 0;
    return `${seen ? 'Diese Liste ist abgearbeitet.' : 'Diese Liste ist leer.'}${replan ? ` ${replan.toLocaleString('de-DE')} warten in der Übersicht auf Neu planen.` : ''}`;
  });

  constructor() {
    this.store.leaveGuard = () => this.canLeave();
    effect(() => {
      const nav = this.store.nav();
      untracked(() => void this.apply(nav));
    });
  }

  ngOnDestroy(): void {
    this.request++;
    if (this.store.leaveGuard) this.store.leaveGuard = null;
  }

  async canLeave(): Promise<boolean> {
    if (this.saving()) return false;
    if (!this.dirty()) return true;
    const saved = await this.viewer()?.saveReviewChanges();
    if (!saved) this.toast.warning('Bitte die Zeichnung abschließen, bevor Sie weitergehen.');
    return !!saved;
  }

  @HostListener('window:beforeunload', ['$event']) beforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty()) event.preventDefault();
  }

  private async apply(nav: CampaignNav): Promise<void> {
    if (!this.store.counts()) await this.store.refresh();
    this.runId.set(nav.runId ?? null);
    this.selection.set(nav.ids ?? null);
    const queue: Queue = nav.ids ? 'selection' : nav.stage ?? this.firstQueue();
    await this.load(queue, nav.focus ?? null, nav.view ?? (queue === 'recheck' ? 'planning' : 'detection'));
  }

  private firstQueue(): Queue {
    const counts = this.store.counts();
    return QUEUES.find(({ id }) => (id === 'recheck' ? this.store.summary()?.recheck : counts?.[id as WorkflowStage]))?.id ?? 'review';
  }

  async open(queue: Queue): Promise<void> {
    if (!(await this.canLeave())) return;
    await this.load(queue, null, queue === 'recheck' ? 'planning' : 'detection');
  }

  /** A row of the rail: a list to go through, or a way to narrow the current one. */
  pick(id: string): void {
    if (id === RUN_ROW) void this.clearRun();
    else if (NARROW_ROWS.some((row) => row.id === id)) void this.narrow(id);
    else if (id !== 'selection') void this.open(id as Queue);
  }

  private async narrow(id: string): Promise<void> {
    if (!(await this.canLeave())) return;
    const next = this.narrowedBy() === id ? null : id;
    this.narrowedBy.set(next);
    const test = NARROW_ROWS.find((row) => row.id === next)?.test;
    const items = this.loadedItems();
    const list = test ? items.filter(test) : items;
    this.list.set(list);
    this.base.set(list.length);
    await this.show(0);
  }

  async clearRun(): Promise<void> {
    if (!(await this.canLeave())) return;
    this.runId.set(null);
    await this.load(this.queue(), null, this.view());
  }

  /** The list stays as loaded, so decided analyses keep their place and can be revisited. */
  private async load(queue: Queue, focus: string | null, view: 'detection' | 'planning'): Promise<void> {
    const request = ++this.request;
    this.queue.set(queue);
    this.view.set(view);
    this.loaded.set(false);
    this.bundle.set(null);
    this.list.set([]);
    this.loadedItems.set([]);
    this.narrowedBy.set(null);
    this.pos.set(0);
    this.decisions.set({});
    this.excluding.set(false);
    this.error.set(null);
    this.bundles.clear();
    try {
      const { items: found, total } = await this.queueItems(queue);
      if (request !== this.request) return;
      let items = found;
      let at = focus ? items.findIndex((row) => row.id === focus) : 0;
      if (focus && at < 0) {
        items = [await this.api.getWorkItem(this.orgId(), this.campaignId(), focus), ...items];
        at = 0;
      }
      if (request !== this.request) return;
      this.base.set(total);
      this.loadedItems.set(items);
      this.list.set(items);
      this.loaded.set(true);
      await this.show(Math.max(0, at), view);
    } catch {
      if (request === this.request) {
        this.loaded.set(true);
        this.error.set(LOAD_FAILED);
      }
    }
  }

  private async queueItems(queue: Queue): Promise<{ items: WorkItem[]; total: number }> {
    if (queue === 'selection') {
      if (!this.store.items()) await this.store.loadItems();
      const known = this.store.itemById();
      const items = (this.selection() ?? []).flatMap((id) => { const item = known.get(id); return item ? [item] : []; });
      return { items, total: items.length };
    }
    const response = await this.api.getWorkItems(this.orgId(), this.campaignId(), {
      ...(queue === 'recheck' ? { recheck: true } : { stage: queue }),
      runId: this.runId(),
      limit: QUEUE_SIZE,
    });
    return { items: response.data, total: response.meta.total };
  }

  retry(): void {
    if (this.list().length) void this.show(this.pos(), this.view());
    else void this.load(this.queue(), null, this.view());
  }

  /** Past one that will not load, without deciding anything about it. */
  skip(): void {
    void this.show(Math.min(this.pos() + 1, this.list().length));
  }

  /** Moving on cancels a load still in flight, so quick key presses are never lost. */
  private async show(index: number, view: 'detection' | 'planning' = this.defaultView()): Promise<void> {
    const request = ++this.request;
    this.pos.set(index);
    this.view.set(view);
    this.excluding.set(false);
    this.error.set(null);
    this.bundle.set(null);
    const target = this.list()[index];
    if (!target) return;
    this.loadingItem.set(true);
    try {
      const [bundle, fresh] = await Promise.all([this.bundleOf(target.id), this.api.getWorkItem(this.orgId(), this.campaignId(), target.id)]);
      if (request !== this.request) return;
      this.bundle.set(bundle);
      this.replace(fresh);
      if (view === 'planning' && !this.planning()) this.view.set('detection');
      const next = this.list()[index + 1];
      if (next) void this.bundleOf(next.id).catch(() => this.bundles.delete(next.id));
    } catch {
      if (request === this.request) {
        this.bundles.delete(target.id);
        this.error.set(LOAD_FAILED);
      }
    } finally {
      if (request === this.request) this.loadingItem.set(false);
    }
  }

  private defaultView(): 'detection' | 'planning' {
    return this.queue() === 'recheck' ? 'planning' : 'detection';
  }

  private bundleOf(id: string): Promise<Bundle> {
    const known = this.bundles.get(id);
    if (known) return known;
    const org = this.orgId();
    const loading = Promise.all([
      this.analyses.getAnalysisDetail(org, id),
      this.analyses.getFilesOverview(org, id),
      this.analyses.getAnalysisPolygons(org, id),
      this.analyses.getAnalysisPlannings(org, id),
    ]).then(([analysis, files, polygons, plannings]) => ({ analysis, files, polygons, plannings }));
    this.bundles.set(id, loading);
    if (this.bundles.size > PREFETCHED) this.bundles.delete(this.bundles.keys().next().value!);
    return loading;
  }

  private replace(item: WorkItem): void {
    const swap = (list: WorkItem[]) => list.map((row) => (row.id === item.id ? item : row));
    this.list.update(swap);
    this.loadedItems.update(swap);
    this.store.patchItem(item);
  }

  private record(id: string, decision: Decision): void {
    this.decisions.update((decisions) => ({ ...decisions, [id]: decision }));
  }

  /** Saved geometry or a drawn roof changed the stage: show it and forget the cached polygons. */
  async onSynced(): Promise<void> {
    const id = this.item()?.id;
    if (!id) return;
    this.bundles.delete(id);
    try {
      const [polygons, fresh] = await Promise.all([
        this.analyses.getAnalysisPolygons(this.orgId(), id),
        this.api.getWorkItem(this.orgId(), this.campaignId(), id),
      ]);
      if (this.item()?.id !== id) return;
      this.bundle.update((bundle) => bundle && { ...bundle, polygons });
      this.replace(fresh);
    } catch {
      this.error.set('Änderung gespeichert, aber der neue Stand konnte nicht geladen werden. Bitte erneut laden.');
    }
  }

  private async write(decision: 'approved' | 'excluded' | 'seen' | 'flagged' | 'checked', mark: Decision, reason?: string): Promise<void> {
    const current = this.item();
    if (!current || this.saving()) return;
    this.saving.set(true);
    this.error.set(null);
    let decided = false;
    try {
      const edited = this.dirty();
      if (!((await this.viewer()?.saveReviewChanges()) ?? true)) {
        this.error.set('Bitte die Zeichnung abschließen. Noch nicht gespeicherte Änderungen bleiben erhalten.');
        return;
      }
      // Our own save changes the detections; judge what is stored now.
      const fresh = await this.api.getWorkItem(this.orgId(), this.campaignId(), current.id);
      if (!edited && fresh.reviewKey !== current.reviewKey) {
        this.replace(fresh);
        this.error.set('Das Ergebnis wurde inzwischen geändert. Bitte prüfen Sie es erneut.');
        return;
      }
      const updated = await this.api.reviewWorkItem(this.orgId(), this.campaignId(), current.id, {
        key: fresh.reviewKey,
        decision,
        ...(reason ? { reason } : {}),
      });
      this.bundles.delete(current.id);
      this.replace(updated);
      this.record(current.id, edited && decision === 'approved' ? 'fix' : mark);
      decided = true;
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Die Entscheidung konnte nicht gespeichert werden.');
    } finally {
      this.saving.set(false);
    }
    if (decided) await this.show(this.pos() + 1);
  }

  /** F: it is right, or the corrections are. */
  confirm(): Promise<void> {
    const item = this.item();
    if (!item || !this.canUpdate()) return Promise.resolve();
    if (!this.dirty() && this.isCheck()) return this.write('checked', 'ok');
    if (this.dirty() || (this.inReview() && item.canApprove)) return this.write('approved', 'ok');
    return Promise.resolve();
  }

  /** M: not right, correct it later. */
  later(): Promise<void> {
    return this.write('flagged', 'later');
  }

  /** →: on, and an untouched result counts as looked at. Corrections are saved first. */
  async next(): Promise<void> {
    if (this.saving() || !this.item()) return;
    if (this.dirty()) return this.confirm();
    if (this.item()!.stage === 'review' && this.canUpdate()) return this.write('seen', 'seen');
    await this.show(Math.min(this.pos() + 1, this.list().length));
  }

  async previous(): Promise<void> {
    if (this.saving() || !this.pos() || !(await this.canLeave())) return;
    await this.show(this.pos() - 1);
  }

  exclude(reason: string): Promise<void> {
    this.excluding.set(false);
    return this.write('excluded', 'out', reason);
  }

  replan(): void {
    const item = this.item();
    if (!item) return;
    this.store.openRun({
      single: this.title(),
      ids: [item.id],
      target: 'replan',
      onStart: () => {
        this.record(item.id, 'plan');
        void this.show(this.pos() + 1);
      },
    });
  }

  async undo(): Promise<void> {
    const current = this.item();
    if (!current || this.saving()) return;
    this.saving.set(true);
    try {
      this.replace(await this.api.undoReview(this.orgId(), this.campaignId(), current.id));
      this.decisions.update(({ [current.id]: _gone, ...rest }) => rest);
    } catch (error) {
      this.error.set(error instanceof Error ? error.message : 'Konnte nicht zurückgenommen werden.');
    } finally {
      this.saving.set(false);
    }
  }

  discard(): void {
    this.viewer()?.discardReviewChanges();
    this.excluding.set(false);
  }

  togglePlanning(): void {
    if (this.hasPlanning()) this.view.update((view) => (view === 'planning' ? 'detection' : 'planning'));
  }

  street(title: string | null): string {
    const text = title ?? 'Ohne Adresse';
    return text.includes(',') ? text.slice(0, text.indexOf(',')) : text;
  }

  /** Letters work wherever focus is, except while typing, in a popup or in the setup panel. */
  @HostListener('window:keydown', ['$event']) onKeyDown(event: KeyboardEvent): void {
    const element = event.target as HTMLElement | null;
    if (
      this.store.runPanel() ||
      element?.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"], [role="listbox"]') ||
      event.ctrlKey || event.metaKey || event.altKey
    )
      return;
    const drawing = this.viewer()?.isDrawingMode();
    const key = event.key.toLowerCase();
    if (!this.excluding() && /^[1-9]$/.test(key) && this.view() === 'detection' && this.viewer()?.assignTypeByKey(Number(key))) {
      event.preventDefault();
      return;
    }
    if (drawing) return;
    if (key === 'escape') { if (this.dirty() || this.excluding()) this.discard(); return; }
    if (this.excluding()) {
      const reason = this.excludeReasons[Number(key) - 1];
      if (reason) { event.preventDefault(); void this.exclude(reason); }
      return;
    }
    const item = this.item();
    if (key === 'arrowright' || key === 'j') void this.next();
    else if (key === 'arrowleft' || key === 'k') void this.previous();
    else if (!item) return;
    else if (key === 'f') void this.confirm();
    else if (key === 'm' && this.inReview() && item.stage !== 'flagged' && !this.dirty() && this.canUpdate()) void this.later();
    else if (key === 'u' && item.review && !this.dirty() && this.canUpdate()) void this.undo();
    else if (key === 'r' && item.stage === 'replan' && !this.dirty() && !this.store.running() && this.store.canStart()) this.replan();
    else if (key === 'z' && item.stage !== 'excluded' && item.stage !== 'running' && this.canUpdate()) this.excluding.set(true);
    else if (key === 'p') this.togglePlanning();
    else return;
    event.preventDefault();
  }
}
